/**
 * Payment method — CARD | UPI — the booking engine reads on prebook and hotel
 * rebook (inside `paymentDetails`) and on promo apply / remove / change (top
 * level, beside `successUrl` / `cancelUrl`).
 *
 * Always CARD for now. There is no Card/UPI picker, and with Razorpay the
 * traveller chooses UPI or card inside Razorpay's own window, which opens after
 * prebook and after any promo call — so the real choice is not known when these
 * requests go out.
 *
 * Stamped in the API functions (`bookHotelAndFlight`, `hotelRebookAPI`,
 * `applyPromoCode` / `removePromoCode` / `changePromoCode`) so every caller
 * carries it. A valid value the caller already set is kept — that is where a
 * future picker plugs in; anything else becomes CARD, so the engine only ever
 * sees one of the two.
 */
export type PaymentMethod = "CARD" | "UPI";

export const DEFAULT_PAYMENT_METHOD: PaymentMethod = "CARD";

export const normalizePaymentMethod = (value: unknown): PaymentMethod => {
  const v = typeof value === "string" ? value.trim().toUpperCase() : "";
  return v === "CARD" || v === "UPI" ? v : DEFAULT_PAYMENT_METHOD;
};

/** `target` with `paymentMethod` set — a `paymentDetails` object or a promo request body. */
export const withPaymentMethod = <T extends object>(target: T): T & { paymentMethod: PaymentMethod } => ({
  ...target,
  paymentMethod: normalizePaymentMethod((target as { paymentMethod?: unknown } | undefined)?.paymentMethod),
});
